import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Table, Tag, Select } from "antd";
import { TableProps } from "antd";
import {
    CheckCircleOutlined,
    StopOutlined,
    PauseCircleOutlined,
    PoweroffOutlined
} from '@ant-design/icons';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import coreAPIService from "../apiServices/coreAPIService";
import { CTW_API_ENDPOINT } from "../utils";
import { toast } from "../common/Popover";
import Block from "../components/block";
import Heading from "../common/Heading";
import { useMetaSetup } from '../context/MetaSetupContext';
import '../assets/css/tablesShadow.css'
import Ad from "./Ad";
const { Option } = Select;

interface AccountDetails {
    adAccountName: String | null
    adAccountId: String | null
}

interface AdData {
    id: string;
    name: string;
    status: string;
    effectiveStatus: string;
    adsetId: string;
    campaignId: string;
    createdTime: string;
    updatedTime: string;
    creativeId: string;
    creativeName: string;
    creativeThumbnailUrl: string;
    previewLink: string;
    spend: string;
}

interface AdResponse {
    meta: {
        status: boolean;
        message: string;
        code: number;
        newToken: string | null;
    },
    data: AdData[]
}


const AdListing: React.FC = () => {

    const { setBreadcrumbs, breadcrumbs } = useBreadcrumbs();
    const [value, setValue] = useState<string | null>();
    const [searchData, setSearchData] = useState<AdData[] | []>([])
    const [tableData, setTableData] = useState<AdData[] | []>([])
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [accountDetails, setAccountDetails] = useState<AccountDetails>()
    const [loading, setLoading] = useState<boolean>(false)
    const [previewLoading, setPreviewLoading] = useState<string | null>(null);
    const { selectedAdAccount } = useMetaSetup();
    const [open, setOpen] = useState(false);
    const [adId, setAdId] = useState<string | null>(null);

    const adAccountName = selectedAdAccount?.name || '';
    const adAccountId = selectedAdAccount?.id || '';


    useEffect(() => {
        if (breadcrumbs.length === 1) {
            setBreadcrumbs([...breadcrumbs,
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate(`/ctc-ads-meta/ad-accounts/ads/${id}`)}>Ad</div>
            }
            ]);
        } else if (breadcrumbs.length === 2 && location.pathname.includes("/ad-accounts/adset")) {
            setBreadcrumbs([...breadcrumbs,
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate(`/ctc-ads-meta/ad-accounts/adset/ads/${id}`, { state: { campaignName: location.state?.campaignName } })}>Ad</div>
            }
            ]);
        }
    }, [setBreadcrumbs]);

    const [dataSource, setDataSource] = useState<AdData[] | []>([]);

    useEffect(() => {
        fetchAdData()
    }, []);

    const fetchAdData = async () => {
        try {
            setLoading(true)
            const res = await coreAPIService.get<AdResponse>(CTW_API_ENDPOINT, `v2/meta-integration/adsets/${id}/ads-with-fields`);

            setLoading(false)
            // Check if the response has the expected `meta` object
            if (res.meta && res.meta.code === 200) {
                setDataSource(res.data)
                setSearchData(res.data)
                setTableData(res.data)
            } else {
                // navigate('/error')
                toast("warning", "Facebook Ad fetch failed.")
            }
            setAccountDetails({ adAccountName: adAccountName, adAccountId: adAccountId })

        } catch (error) {
            // navigate('/error')
            toast("error", "Error occured while fetching Facebook Ads.")
        }

    }




    const handleSwitchChange = (checked: boolean, record: AdData) => {
        const updatedData = dataSource.map((item) =>
            item.id === record.id ? { ...item, enable: checked } : item
        );
        setDataSource(updatedData);
    };

    const handlePreviewClick = async (adId: string) => {
        try {
            setPreviewLoading(adId);

            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/meta-integration/ads/${adId}/previewLink`
            );

            if (res.meta?.code === 200 && res.data?.previewShareableLink) {
                const link = res.data.previewShareableLink;

                // Fallback
                const textarea = document.createElement("textarea");
                textarea.value = link;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);

                toast("success", "Preview link copied to clipboard!");
            } else {
                toast("warning", "Failed to fetch preview link.");
            }
        } catch (error) {
            console.error("Error fetching preview link:", error);
            toast("error", "Error occurred while fetching preview link.");
        } finally {
            setPreviewLoading(null);
        }
    };


    const columns: TableProps<AdData>['columns'] = [
        // {
        //     title: "Enable/Disable",
        //     dataIndex: "enable",
        //     key: "enable",
        //     render: (enable: boolean, record: AdData) => (
        //         <Switch
        //             size="small"
        //             checked={enable}
        //             onChange={(checked) => handleSwitchChange(checked, record)}
        //         />
        //     ),
        //     align: "center",
        // },
        {
            title: "Ad Name",
            dataIndex: "name",
            key: "name",
            render: (enable: boolean, record: AdData) => (
                <div>
                    {record.name} <br />
                    {/* <a className="text-blue-600 cursor-pointer" onClick={() => navigate(`/ctc-ads-meta/ad/${record.id}`, { state: { ad_name: record.name } })}>View Ad →</a> */}
                    <a className="text-blue-600 cursor-pointer" onClick={() => { setOpen(true); setAdId(record.id) }}>View Ad →</a>
                </div>
            ),
            align: "center",
        },
        {
            title: "Status",
            dataIndex: "effectiveStatus",
            key: "effectiveStatus",
            render: (enable: boolean, record: AdData) => {
                let color: string;
                let icon;
                switch (record.effectiveStatus.toLowerCase()) {
                    case "active":
                        color = "green";
                        icon = <CheckCircleOutlined />
                        break;
                    case "inactive":
                        color = "red";
                        icon = <StopOutlined />
                        break;
                    case "disabled":
                        color = "gray";
                        icon = <PoweroffOutlined />
                        break;
                    case "paused":
                        color = "purple";
                        icon = <PauseCircleOutlined />
                        break;
                    default:
                        color = "blue";
                    // icon = <PoweroffOutlined />
                }
                return <Tag icon={icon} color={color}>{record.effectiveStatus}</Tag>;
            },
            align: "center",
        },
        {
            title: "Spend",
            dataIndex: "spend",
            key: "spend",
            align: "center",
            render: (enableButton: boolean, record: AdData) => { return <>{record.spend ? `₹${record.spend}` : "-"}</> },
        },
        {
            title: "Created Date",
            dataIndex: "createdTime",
            key: "createdTime",
            align: "center",
            render: (enableButton: boolean, record: AdData) => { return <>{record.createdTime.split("T")?.[0]}</> },
        },
        {
            title: "Thumbnail Image",
            dataIndex: "creativeThumbnailUrl",
            key: "creativeThumbnailUrl",
            align: "center",
            render: (enableButton: boolean, record: AdData) => { return <img className="m-auto" src={record.creativeThumbnailUrl || ""}></img> },
        },
        {
            title: "Copy Preview Link",
            dataIndex: "previewLink",
            key: "previewLink",
            align: "center",
            render: (enableButton: boolean, record: AdData) => {
                return (
                    <a
                        className={`text-blue-600 cursor-pointer ${previewLoading === record.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => previewLoading !== record.id && handlePreviewClick(record.id)}
                    >
                        {previewLoading === record.id ? 'Loading...' : 'Copy link'}
                    </a>
                );
            },
        },
        ...(location.state?.isTimesPanelCampaign ? [{
            title: "WhatsApp Number",
            dataIndex: "whatsappNumber",
            key: "whatsappNumber",
            render: (enableButton: boolean, record: AdData) => { return <>{location.state?.whatsAppNumber ? <a className="text-blue-600 cursor-pointer" href={`https://wa.me/${location.state?.whatsAppNumber}?text=Hello`} target="_blank">{location.state?.whatsAppNumber}</a> : "-"}</> },
        }] : []),
    ];


    const handleSearch = (newValue: string) => {
        const filteredData = (dataSource || []).filter((d) =>
            typeof d.name === "string" &&
            d.name.toLocaleLowerCase().includes(newValue.toLocaleLowerCase())
        );
        setSearchData(filteredData);
    };

    const handleChange = (newValue: string) => {
        const filteredData = (dataSource || []).filter((d) =>
            d.id === newValue
        );
        setTableData(filteredData);
        setValue(newValue)
    };

    const handleStatusChange = (newValue: string) => {
        setValue(null)

        if (newValue === "ALL") {
            setTableData(dataSource)
            return
        }

        const filteredData = (dataSource || []).filter((d) =>
            typeof d.effectiveStatus === "string" &&
            d.effectiveStatus.toLocaleLowerCase().includes(newValue.toLocaleLowerCase())
        );
        setTableData(filteredData);
    };

    return (
        <>
            {!open ? <Block>
                <Heading title={"Ads"} />
                <div className="flex align-base mb-5 justify-between">
                    <div className="grid grid-cols-2 items-center gap-x-8">
                        <p>
                            <strong>Meta Ad Account: </strong>{accountDetails?.adAccountName} &nbsp;&nbsp;
                        </p>
                        <p>
                            <strong>Campaign Name: </strong> {location.state?.campaignName} &nbsp;&nbsp;
                        </p>
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>

                        <Select
                            showSearch
                            value={value}
                            placeholder="Search by Ad name"
                            className="w-80"
                            optionFilterProp="label"
                            allowClear
                            onChange={(val) => {
                                if (!val) {
                                    handleStatusChange("ALL");
                                } else {
                                    handleChange(val);
                                }
                            }}
                            options={(searchData || []).map((d) => ({
                                value: d.id,
                                label: `${d.name} (${d.id})`,
                            }))}
                        />

                        <Select
                            placeholder="Select Ad status"
                            style={{ width: 200 }}
                            onChange={handleStatusChange}
                        >
                            <Option value="ALL">All</Option>
                            <Option value="ACTIVE">Active</Option>
                            <Option value="INACTIVE">Inactive</Option>
                            <Option value="DISABLED">Disabled</Option>
                            <Option value="PAUSED">Paused</Option>
                        </Select>
                    </div>
                </div>
                <div className="p-4 bg-gray-50">

                    {loading ? <Table
                        columns={columns}
                        dataSource={[]}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 'max-content' }}
                        loading={true}
                    /> :
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            pagination={{ pageSize: 10 }}
                        />}
                </div>
            </Block>
                :
                <Ad setOpen={setOpen} adId={adId || ""} />}
        </>
    );
};

export default AdListing;

